import { useState, useEffect, useRef } from 'react'

function parseLinkHeader(string) {
  const output = {}
  const regex = /<([^>]+)>; rel="([^"]+)"/g

  let match

  while (match = regex.exec(string)) {
    const [_, val, key] = match
    output[key] = val
  }

  return output
}

export const encodeParams = (params, obj) => {
  Object.entries(obj).map(([key, value]) => {
    if (Array.isArray(value)) {
      value.map((v) => {
        if (params.has(`search[${key}]`)) {
          let prevParam = params.get(`search[${key}]`);
          params.delete(`search[${key}]`);
          params.append(`search[${key}]`, prevParam + ',' + v.value)
        } else {
          params.append(`search[${key}]`, v.value)
        }
        params.append(`${key}[]`, v.value)
      })
    } else if (typeof value == 'object' && typeof value?.value !== 'undefined' && value?.value !== null) {
      params.append(key, value.value)
      params.append(`search[${key}]`, value.value)
    } else if (!!value) {
      params.append(key, value)
      params.append(`search[${key}]`, value)
    }
  })

  return params
}

export default function useApi(urlOrOptions = null, maybeOptions = null) {
  const [initialOptions, setInitialOptions] = useState({
    load: true,
    ...(maybeOptions || {}),
    ...(typeof urlOrOptions === 'object' ? urlOrOptions : { url: urlOrOptions }),
  })

  const {
    pagination: _pagination,
    sorting: _sorting,
    url: _url,
    ...fetchOptions
  } = initialOptions

  const skipLoading = useRef(!initialOptions.load)
  const [options, setOptions] = useState(fetchOptions)

  const [url, setUrl] = useState(_url)
  const [loading, setLoading] = useState(false)
  const [params, setParams] = useState({})
  const [sorting, setSorting] = useState({
    key: _sorting?.key || null,
    direction: _sorting?.direction || 'ASC',
  })

  useEffect(() => {
    setUrl(urlOrOptions?.url || urlOrOptions)
  }, [urlOrOptions])

  const [pagination, setPagination] = useState({
    total: null,
    page: 0,
    pageSize: null,
    totalPages: null,
    next: () => {},
    prev: () => {},
    last: () => {},
    first: () => {},
    ..._pagination,
  })

  const [data, setData] = useState(null)
  const [meta, setMeta] = useState(null)
  const [errors, setErrors] = useState([])

  const setStateFromUrl = (url) => {
    const { searchParams } = new URL(url, window.location.origin)
    
    setPagination((prev) => ({
      ...prev,
      page: searchParams.get('page'),
      pageSize: searchParams.get('pageSize'),
    }))

    setSorting((prev) => ({
      ...prev,
      key: searchParams.get('orderBy'),
      direction: searchParams.get('orderDirection'),
    }))
  }

  const onFetch = (signal) => {
    setLoading(true)
    setErrors([])

    let _url = new URL(url, window.location.origin)

    if (params) {
      encodeParams(_url.searchParams, params)
    }

    if (pagination?.pageSize && !_url.searchParams.has('page')) {
      _url.searchParams.set('page', pagination.page)
      _url.searchParams.set('pageSize', pagination.pageSize)
    }

    if (sorting?.key && !_url.searchParams.has('orderDirection')) {
      _url.searchParams.set('orderBy', sorting.key)
      _url.searchParams.set('orderDirection', sorting.direction)
    }

    if (initialOptions.includeRelations) {
      _url.searchParams.set('includeRelations', 1)
    }

    const fetchOpts = {
      body: options.json ? JSON.stringify(options.json) : undefined,
      credentials: 'same-origin',
      signal,
      ...options,
    }

    fetch(_url, fetchOpts)
      .then((response) => {
        const link = response.headers.get('Link')

        if (link) {
          const links = parseLinkHeader(link)
          const nextPagination = {
            total: parseInt(response.headers.get('X-Pagination-Total')) || null,
            page: parseInt(response.headers.get('X-Pagination-Page')) || pagination?.page,
            pageSize: parseInt(response.headers.get('X-Pagination-PageSize')) || null,
            first: () => setStateFromUrl(links.first),
            last: () => setStateFromUrl(links.last),
            next: () => setStateFromUrl(links.next),
            prev: () => setStateFromUrl(links.prev),
            go: (i) => setPagination((prev) => ({ ...prev, page: i }))
          }

          nextPagination.totalPages = Math.ceil(nextPagination.total / nextPagination.pageSize)

          setPagination(nextPagination)
        }

        return response.json()
      })
      .then((result) => {
        setData(result.success ? result.data : result)
        setErrors(result.errors || [])
        setMeta(result.meta || null)
        setLoading(false)
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          window.alert(err)
        }
      })
  }

  const refetch = (urlOrOptions = null, maybeOptions = null) => {
    skipLoading.current = false

    const nextOptions = {
      ...(maybeOptions || {}),
      ...(typeof urlOrOptions === 'object' ? urlOrOptions : { url: urlOrOptions }),
    }

    const {
      pagination: _pagination,
      sorting: _sorting,
      url: _url,
      params: _params,
      ...fetchOptions
    } = nextOptions

    setOptions((prev) => ({ ...prev, ...fetchOptions }))
    setSorting((prev) => ({ ...prev, ..._sorting }))
    setPagination((prev) => ({ ...prev, ..._pagination }))
    setParams(_params)
    setUrl((prev) => _url ? _url : prev)
  }

  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller

    if (skipLoading.current || !url) {
      return
    }

    onFetch(signal)

    return () => {
      controller.abort()
    }
  }, [
    url,
    options,
    params,
    sorting,
    pagination.page,
  ])

  return [data, loading, errors, refetch, meta, pagination]
}
